// /opt/isp_erp/frontend/src/components/tickets/MaterialBuscador.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios from '../../api/axios';

/**
 * Buscador de materiales para los cierres de ticket.
 * modo="stock": solo lo que el técnico tiene. modo="catalogo": todo el catálogo, mostrando el stock del técnico.
 * onSelect(item | null) recibe { material_id, codigo, nombre, unidad, disponible }.
 */
const MaterialBuscador = ({ ticketId, tecnicoId, modo = 'stock', value, onSelect, disabled = false }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState(null);
  const contador = useRef(0);

  useEffect(() => {
    if (!value) setSeleccion(null);
  }, [value]);

  useEffect(() => {
    if (!abierto || !ticketId) return undefined;
    const id = ++contador.current;
    const espera = setTimeout(async () => {
      setCargando(true);
      try {
        const params = { modo, limite: 50 };
        if (texto.trim()) params.q = texto.trim();
        if (tecnicoId) params.tecnico_id = tecnicoId;
        const r = await axios.get(`/tickets/${ticketId}/materiales-disponibles`, { params });
        if (id === contador.current) {
          setItems(r.data.items || []);
          setTotal(r.data.total || 0);
        }
      } catch (e) {
        if (id === contador.current) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (id === contador.current) setCargando(false);
      }
    }, 300);
    return () => clearTimeout(espera);
  }, [abierto, texto, ticketId, tecnicoId, modo]);

  const etiqueta = (o) => (o ? `${o.nombre}${o.codigo ? ` (${o.codigo})` : ''}` : '');

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      open={abierto}
      onOpen={() => setAbierto(true)}
      onClose={() => setAbierto(false)}
      options={items}
      value={seleccion}
      loading={cargando}
      filterOptions={(opciones) => opciones}
      isOptionEqualToValue={(o, v) => o.material_id === v.material_id}
      getOptionLabel={etiqueta}
      onInputChange={(_, v, motivo) => {
        if (motivo === 'input') setTexto(v);
        if (motivo === 'clear') setTexto('');
      }}
      onChange={(_, nuevo) => {
        setSeleccion(nuevo);
        onSelect(nuevo);
      }}
      noOptionsText={cargando ? 'Buscando...' : (modo === 'stock' ? 'El técnico no tiene materiales que coincidan' : 'Sin resultados')}
      renderOption={(props, o) => {
        const { key, ...resto } = props;
        const sinStock = Number(o.disponible) <= 0;
        return (
          <li key={o.material_id} {...resto}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{o.nombre}</Typography>
              <Typography variant="caption" color={sinStock ? 'error' : 'text.secondary'}>
                {o.codigo ? `${o.codigo} · ` : ''}
                {sinStock ? 'El técnico no tiene stock' : `Disponible: ${o.disponible} ${o.unidad || ''}`}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Material (escribe para buscar)"
          helperText={abierto && total ? `${items.length} de ${total} · escribe para filtrar` : ' '}
          FormHelperTextProps={{ sx: { fontSize: '0.7rem', mt: 0.3 } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {cargando ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MaterialBuscador;
